/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { UserEdition } from "@encoo-web/encoo-lib";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Table,
  Tabs,
  Tag,
} from "antd";
import copy from "copy-to-clipboard";
import moment from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import CreateEmailSuffix from "src/containers/enterprise/components/CreateEmailSuffix";
import RenewContract from "src/containers/enterprise/components/RenewContract";
import ViewDetail from "src/containers/enterprise/components/ViewDetail";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { EnterpriseDetailList } from "src/models/company";
import { LicenseDetail } from "src/models/enterprise";
import { AccountSeviceClient } from "src/services/CompanyService";

const { TextArea } = Input;
const formItemColor = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  line-height: 14px;
  color: rgb(61, 73, 102);
  font-weight: 600;
`;
const divColor = css`
  margin-top: 12px;
  color: rgb(143, 155, 179);
  background-color: rgb(247, 249, 252);
  padding: 10px 13px;
  min-height: 32px;
`;

export default React.memo(
  (props: {
    isShowDetail: boolean;
    showDetail: EnterpriseDetailList;
    onClose: () => void;
    fetch: () => void;
  }) => {
    const { isShowDetail, showDetail, onClose, fetch } = props;
    const [form] = Form.useForm();
    const formatMessage = useFormatMessage();
    const [isEdit, setIsEdit] = useState(false);
    const [suffix, setSuffix] = useState(false);
    const [suffixContent, setSuffixContent] = useState<string[]>([]);
    const [isSuffix, setIsSuffix] = useState(false);
    const [isRenew, setIsRenew] = useState(false);
    const [activeKey, setActiveKey] = useState("1");
    const [isView, setIsView] = useState(false);
    const [viewDetail, setViewDetail] = useState({} as LicenseDetail);
    const [dataSource, setDataSource] = useState<LicenseDetail[]>([]);
    // const isDomin = showDetail?.shouldVerifyDomain === true ? 0 : 1;
    const edition = UserEdition[
      showDetail?.edition as UserEdition
    ] as unknown as number;

    useEffect(() => {
      let isUnmounted = false;
      if (!isUnmounted) {
        if (showDetail) {
          setIsSuffix(
            showDetail?.shouldVerifyDomain
              ? showDetail?.shouldVerifyDomain
              : false
          );
          setSuffixContent(showDetail?.tags as string[]);
        }
        if (isShowDetail) {
          form.setFieldsValue({
            name: showDetail?.name,
            description: showDetail?.description,
            shouldVerifyDomain: showDetail?.shouldVerifyDomain === true ? 0 : 1,
          });
        }
      }
      return () => {
        isUnmounted = true;
      };
    }, [form, isShowDetail, showDetail]);

    useEffect(() => {
      if (isShowDetail && activeKey === "2") {
        AccountSeviceClient.licenseDetail(showDetail?.id).then((res) => {
          setDataSource(res.data.data);
        });
      }
    }, [activeKey, isShowDetail, showDetail]);

    const onViewClose = useCallback(() => {
      setIsView(false);
    }, []);

    const onViewDetail = useCallback((record) => {
      setIsView(true);
      setViewDetail(record);
    }, []);

    const columns = useMemo(
      () => [
        {
          title: formatMessage(messageIds.license.common.fromTo),
          dataIndex: "fromTo",
          ellipsis: true,
          width: "27%",
          render: (text: string, record: LicenseDetail) => (
            <span>
              {moment(record?.validFrom).format("YYYY-MM-DD HH:mm:ss")} ~
              {moment(record?.validTo).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.createdAt),
          dataIndex: "createAt",
          ellipsis: true,
          width: "27%",
          render: (text: string, record: LicenseDetail) => (
            <span>
              {moment(record?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.userImported),
          dataIndex: "name",
          ellipsis: true,
          width: "27%",
        },
        {
          title: formatMessage(messageIds.common.operation),
          dataIndex: "operation",
          width: "19%",
          render: (text: string, record: LicenseDetail) => (
            <Button
              type="link"
              onClick={() => onViewDetail(record)}
              css={css`
                padding-left: 0;
              `}
            >
              {formatMessage(messageIds.common.view)}
            </Button>
          ),
        },
      ],
      [formatMessage, onViewDetail]
    );

    const onRenew = useCallback(() => {
      setIsRenew(!isRenew);
    }, [isRenew]);

    const onModalCancel = useCallback(() => {
      onClose();
      setActiveKey("1");
      setIsEdit(false);
      form.resetFields();
    }, [form, onClose]);

    const onEdit = useCallback(() => {
      setIsEdit(true);
    }, []);

    const onCancel = useCallback(() => {
      setIsEdit(false);
      form.resetFields();
    }, [form]);

    const suffixChange = useCallback(() => {
      setSuffix(!suffix);
    }, [suffix]);

    const getSuffix = useCallback(
      (suffix: string) => {
        if (suffixContent) {
          if (suffixContent.length > 0) {
            if (suffixContent.indexOf(suffix) === -1) {
              suffixContent.push(suffix);
            } else {
              message.error(
                formatMessage(messageIds.company.validate.emailSuffixRepeat)
              );
            }
          } else {
            suffixContent.push(suffix);
          }
        }
      },
      [formatMessage, suffixContent]
    );

    const onTagClose = useCallback(
      (e) => {
        if (suffixContent) {
          const tag = suffixContent.filter((tag) => tag !== e);
          setSuffixContent(tag);
        }
      },
      [suffixContent]
    );

    const onSave = useCallback(async () => {
      const formContent = await form.validateFields();
      const formValue = {
        name: formContent.name as string,
        description: formContent.description as string,
      };
      const secondFormValue = {
        tags: suffixContent as string[],
      };
      let thirdFormValue = {};
      if (formContent.password) {
        thirdFormValue = {
          ownerUserPassword: formContent.password as string,
        };
      }
      const enterpriseData = Object.assign(
        formValue,
        secondFormValue,
        thirdFormValue
      );
      const id = showDetail?.id as string;
      AccountSeviceClient.update({
        id,
        enterpriseData,
      }).then(() => {
        fetch();
        message.success(formatMessage(messageIds.common.updateSuccessfully));
      });
      onModalCancel();
    }, [fetch, form, formatMessage, onModalCancel, showDetail, suffixContent]);

    const onCopy = useCallback(() => {
      copy(showDetail?.id || "");
      message.success(formatMessage(messageIds.common.copySuccessfully));
    }, [formatMessage, showDetail]);

    return (
      <div>
        <Modal
          visible={isShowDetail}
          width="40%"
          onCancel={onModalCancel}
          footer={""}
          css={css`
            top: 20px;
          `}
        >
          <Tabs
            type="card"
            activeKey={activeKey}
            tabBarStyle={{
              height: "30px",
              lineHeight: "30px",
            }}
            tabBarGutter={0}
            onChange={(activekey) => {
              setActiveKey(activekey);
            }}
            css={css`
              .ant-tabs-tab {
                background-color: #ffffff !important;
                border-radius: 4px 0px 0px 0px;
                width: 130px;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid #ffffff;
              }
              .ant-tabs-tab.ant-tabs-tab-active {
                background-color: #84adff !important;
              }
              .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                color: white;
              }
            `}
          >
            <Tabs.TabPane
              tab={formatMessage(messageIds.company.setting.name)}
              key="1"
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.company.setting.title)}
                </h1>
                {isEdit ? (
                  <div>
                    <Button onClick={onCancel}>
                      {formatMessage(messageIds.common.cancel)}
                    </Button>
                    <Button
                      type="primary"
                      css={css`
                        padding: 4px 15px;
                        font-size: 12px;
                        border: 1px solid rgb(51, 119, 255);
                        border-radius: 4px;
                        height: auto;
                        margin-left: 20px;
                      `}
                      onClick={onSave}
                    >
                      {formatMessage(messageIds.common.save)}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Button
                      type="primary"
                      css={css`
                        padding: 4px 15px;
                        font-size: 12px;
                        border: 1px solid rgb(51, 119, 255);
                        border-radius: 4px;
                        height: auto;
                        margin-right: 20px;
                      `}
                      onClick={onRenew}
                    >
                      {formatMessage(messageIds.company.renewContract)}
                    </Button>
                    <Button
                      type="primary"
                      css={css`
                        padding: 4px 15px;
                        font-size: 12px;
                        border: 1px solid rgb(51, 119, 255);
                        border-radius: 4px;
                        height: auto;
                      `}
                      onClick={onEdit}
                    >
                      {formatMessage(messageIds.common.edit)}
                    </Button>
                  </div>
                )}
              </div>
              {
                <Fragment>
                  <Form
                    form={form}
                    layout="vertical"
                    css={css`
                      max-height: 80vh;
                      overflow-y: auto;
                      overflow-x: hidden;
                    `}
                  >
                    <Row gutter={20}>
                      <Col span={12}>
                        <Form.Item
                          label={
                            <Fragment>
                              <span>
                                {formatMessage(
                                  messageIds.company.setting.customerIdentifier
                                )}
                                :
                              </span>
                              <Button type="link" onClick={onCopy}>
                                {formatMessage(messageIds.common.copy)}
                              </Button>
                            </Fragment>
                          }
                          name="id"
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.id}</div>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.companyType
                          )}：`}
                          css={formItemColor}
                          style={{ paddingTop: "10px" }}
                        >
                          <div css={divColor}>
                            {edition === 0
                              ? formatMessage(messageIds.edition.enterprise)
                              : formatMessage(messageIds.edition.community)}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.companyName
                          )}：`}
                          name="name"
                          css={formItemColor}
                          initialValue={showDetail?.name}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.company.validate.inputCompanyName
                              ),
                            },
                            {
                              max: 256,
                              message: formatMessage(
                                messageIds.company.validate.stringLength,
                                {
                                  name: formatMessage(
                                    messageIds.company.companyName
                                  ),
                                  min: 1,
                                  max: 256,
                                }
                              ),
                            },
                            {
                              whitespace: true,
                            },
                          ]}
                        >
                          {isEdit ? (
                            <Input></Input>
                          ) : (
                            <div css={divColor}>{showDetail?.name}</div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.common.description
                          )}：`}
                          name="description"
                          css={formItemColor}
                          initialValue={showDetail?.description}
                          rules={[
                            {
                              max: 500,
                              message: formatMessage(
                                messageIds.company.validate.stringLength,
                                {
                                  name: formatMessage(
                                    messageIds.common.description
                                  ),
                                  min: 0,
                                  max: 500,
                                }
                              ),
                            },
                          ]}
                        >
                          {isEdit ? (
                            showDetail?.description ? (
                              <TextArea
                                value={showDetail?.description}
                              ></TextArea>
                            ) : (
                              <TextArea
                                placeholder={formatMessage(
                                  messageIds.common.pleaseInput
                                )}
                              ></TextArea>
                            )
                          ) : (
                            <div css={divColor} style={{ height: "100px" }}>
                              {showDetail?.description}
                            </div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={20}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.common.createdBy
                          )}：`}
                          name="createdByName"
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.createdByName}</div>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.common.creationTime
                          )}：`}
                          name="createdAt"
                          css={formItemColor}
                        >
                          <div css={divColor}>
                            {moment(showDetail?.createdAt).format(
                              "YYYY-MM-DD HH:mm:ss"
                            )}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={
                            <Fragment>
                              <span>
                                {formatMessage(
                                  messageIds.company.isFixedPostfixMailbox
                                )}
                              </span>
                              {isEdit && isSuffix ? (
                                <Button type="link" onClick={suffixChange}>
                                  {formatMessage(messageIds.company.addSuffix)}
                                </Button>
                              ) : (
                                ""
                              )}
                            </Fragment>
                          }
                          name="shouldVerifyDomain"
                          css={formItemColor}
                        >
                          <div css={divColor}>
                            {showDetail?.shouldVerifyDomain === true
                              ? formatMessage(messageIds.common.yes)
                              : formatMessage(messageIds.common.no)}
                          </div>
                          {/* {isEdit ? (
                            <Select value={isDomin} onChange={onSelectChange}>
                              <Option value={0}>
                                {formatMessage(messageIds.common.yes)}
                              </Option>
                              <Option value={1}>
                                {formatMessage(messageIds.common.no)}
                              </Option>
                            </Select>
                          ) : (
                            <div css={divColor}>
                              {showDetail?.shouldVerifyDomain === true
                                ? formatMessage(messageIds.common.yes)
                                : formatMessage(messageIds.common.no)}
                            </div>
                          )} */}
                        </Form.Item>
                      </Col>
                    </Row>
                    {isSuffix ? (
                      <Row>
                        <Col span={24}>
                          <Form.Item
                            label={`${formatMessage(
                              messageIds.company.mailboxPostfixConfiguration
                            )}：`}
                            name="suffix"
                            css={formItemColor}
                          >
                            <div
                              css={css`
                                flex: auto;
                                max-width: 100%;
                              `}
                            >
                              <div
                                css={css`
                                  margin-top: 12px;
                                  padding: 4px 10px;
                                  min-height: 32px;
                                  color: rgb(143, 155, 179);
                                  background-color: rgb(247, 249, 252);
                                `}
                              >
                                {suffixContent
                                  ? suffixContent.map((item) => (
                                      <Tag
                                        key={item}
                                        closable={isEdit ? true : false}
                                        onClose={() => onTagClose(item)}
                                      >{`@${item}`}</Tag>
                                    ))
                                  : ""}
                              </div>
                            </div>
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : (
                      ""
                    )}
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.initializeAdministratorAccount
                          )}：`}
                          name="email"
                          css={formItemColor}
                        >
                          <div css={divColor}>{showDetail?.ownerEmail}</div>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.initializeAdministratorPassword
                          )}：`}
                          name="password"
                          css={formItemColor}
                        >
                          {isEdit ? (
                            <div css={divColor}>
                              <Input.Password
                                placeholder={formatMessage(
                                  messageIds.common.pleaseInput
                                )}
                                css={css`
                                  margin: 0 0 24px;
                                `}
                                autoComplete="off"
                              ></Input.Password>
                              <span
                                css={css`
                                  color: rgb(91, 143, 249);
                                `}
                              >
                                {`*${formatMessage(
                                  messageIds.company.updatePasswordTips
                                )}`}
                              </span>
                            </div>
                          ) : (
                            <div css={divColor}>******************</div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.company.companySize
                          )}：`}
                          name="companySize"
                          css={formItemColor}
                          initialValue={showDetail?.companySize}
                        >
                          <div css={divColor}>{showDetail?.companySize}</div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Fragment>
              }
              <CreateEmailSuffix
                suffix={suffix}
                suffixChange={suffixChange}
                getSuffix={getSuffix}
              ></CreateEmailSuffix>
              <RenewContract
                isRenew={isRenew}
                onRenew={onRenew}
                companyId={showDetail?.companyId as string}
              ></RenewContract>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={formatMessage(messageIds.license.common.licenseInformation)}
              key="2"
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.license.common.licenseInformation)}
                </h1>
              </div>
              <div
                css={css`
                  margin-top: 20px;
                `}
              >
                <Table columns={columns} dataSource={dataSource}></Table>
              </div>
            </Tabs.TabPane>
          </Tabs>
        </Modal>
        <ViewDetail
          isView={isView}
          viewDetail={viewDetail}
          onViewClose={onViewClose}
        ></ViewDetail>
      </div>
    );
  }
);
